// src/hooks/useSEO.js
// Componentes para gestionar meta tags, Open Graph y Schema.org por página
import { Helmet } from "react-helmet-async";

const BASE_URL   = "https://www.aiveo.net";
const SITE_NAME  = "AIVEO";
const DEFAULT_IMG = `${BASE_URL}/og-image.png`;

/**
 * Construye el objeto Helmet con todos los meta tags necesarios.
 * No es un hook propiamente dicho — exporta el componente <PageSEO> para usar en JSX.
 */

export function PageSEO({
  title,
  description,
  image,
  url,
  type = "website",
  schema = null,
}) {
  const fullTitle = title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — AI Cinema Platform`;

  const metaDesc = description ||
    "Descubre series, películas, libros y cómics originales creados con inteligencia artificial. Solo en AIVEO.";

  const metaImg  = image  || DEFAULT_IMG;
  const metaUrl  = url    ? `${BASE_URL}${url}` : BASE_URL;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={metaUrl} />

      {/* Open Graph */}
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image"       content={metaImg} />
      <meta property="og:url"         content={metaUrl} />
      <meta property="og:type"        content={type} />
      <meta property="og:site_name"   content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image"       content={metaImg} />

      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

/** Schema.org para una serie de TV */
export function buildTVSeriesSchema({ title, synopsis, thumbnail, releaseDate, genres, episodes, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "TVSeries",
    "name": title,
    "description": synopsis,
    "image": thumbnail,
    "datePublished": releaseDate,
    "genre": genres,
    "numberOfEpisodes": episodes,
    "url": `${BASE_URL}${url}`,
    "productionCompany": {
      "@type": "Organization",
      "name": "AIVEO"
    },
    "inLanguage": "es",
    "contentRating": "TP",
    "creator": {
      "@type": "Organization",
      "name": "AIVEO AI"
    }
  };
}

/** Schema.org para una película */
export function buildMovieSchema({ title, synopsis, thumbnail, releaseDate, genres, duration, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "Movie",
    "name": title,
    "description": synopsis,
    "image": thumbnail,
    "datePublished": releaseDate,
    "genre": genres,
    "duration": duration,
    "url": `${BASE_URL}${url}`,
    "productionCompany": {
      "@type": "Organization",
      "name": "AIVEO"
    },
    "inLanguage": "es",
    "contentRating": "TP",
    "director": {
      "@type": "Person",
      "name": "AIVEO AI"
    }
  };
}

/** Schema.org para un libro */
export function buildBookSchema({ title, synopsis, thumbnail, releaseDate, genres, author, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": title,
    "description": synopsis,
    "image": thumbnail,
    "datePublished": releaseDate,
    "genre": genres,
    "author": {
      "@type": "Person",
      "name": author || "AIVEO AI"
    },
    "url": `${BASE_URL}${url}`,
    "publisher": {
      "@type": "Organization",
      "name": "AIVEO"
    },
    "inLanguage": "es"
  };
}
